import React from 'react';
import propTypes from 'prop-types';
import Slider from 'react-slick';
import { useLabels } from 'wsm-common-data';
import 'slick-carousel/slick/slick.scss';
import Img from './Img';
import Typography from './Typography';
import defaultPropValues from '../helper/defaultPropValues';
import './ImageCarousel.scss';

const ImageCarousel = (props) => {
	const data = { ...defaultPropValues.carouselProps.data, ...props.data };
	const {
		autoplay,
		paginationThumbnails,
		images,
		swatches,
		enableSwatches,
		swatchTextColor
	} = data;

	const labels = useLabels();
	const href = '#';

	const settings = {
		dots: true,
		autoplay: autoplay.enabled,
		autoplaySpeed: autoplay.interval,
		customPaging: (i) => {
			if (swatches && enableSwatches) {
				return (
					<a href={href}>
						<Typography
							variant="p"
							className={`swatch-text text-${swatchTextColor}`}
						>
							{labels.get(swatches[i].swatchName)}
						</Typography>
						<Img
							src={swatches[i].swatchSrc}
							alt={labels.get(swatches[i].swatchName)}
						/>
					</a>
				);
			} else if (paginationThumbnails && paginationThumbnails.enabled) {
				return (
					<a href={href}>
						<Img
							src={images[i].src}
							alt={labels.get(images[i].alt)}
						/>
						{paginationThumbnails.captions && (
							<Typography variant="p">
								{labels.get(images[i].caption)}
							</Typography>
						)}
					</a>
				);
			} else {
				return (
					<input
						aria-label={`Carousel pagination button ${i + 1}`}
						type="button"
					/>
				);
			}
		},
		swipe: !enableSwatches,
		arrows: false,
		speed: enableSwatches ? 0 : 500
	};

	const imageSlider = () => {
		return images.map((image) => (
			<div key={image.alt} className="position-relative">
				<Img src={image.src} alt={labels.get(image.alt)} />
				{image.caption && (
					<Typography
						variant="span"
						className="absolute-bottom text-center w-100 mb-3"
					>
						{labels.get(image.caption)}
					</Typography>
				)}
			</div>
		));
	};

	const swatchSlider = () => {
		return swatches.map((swatch) => (
			<div key={swatch.imageAlt} className="position-relative">
				<Img src={swatch.imageSrc} alt={labels.get(swatch.imageAlt)} />
				{swatch.disclaimer && (
					<React.Fragment>
						<Typography
							variant="span"
							className="absolute-bottom text-inverse text-right mb-3 swatch-disclaimer-text"
						>
							{labels.get(swatch.disclaimer)}
						</Typography>
						<Typography
							variant="div"
							className="text-center mt-4 d-sm-none"
						>
							{labels.get(swatch.disclaimer)}
						</Typography>
						<Typography
							variant="div"
							className="text-center mt-4 d-sm-none"
						>
							{labels.get(swatch.swatchName)}
						</Typography>
					</React.Fragment>
				)}
			</div>
		));
	};

	return (
		<Slider
			{...settings}
			className={`${
				paginationThumbnails.enabled ? 'pagination-thumbnails' : ''
			} ws-content-rows-image-carousel ${
				enableSwatches ? 'swatch-row' : ''
			}`}
		>
			{enableSwatches ? swatchSlider() : imageSlider()}
		</Slider>
	);
};

export default ImageCarousel;

export const ImageCarouselProps = {
	data: propTypes.shape({
		images: propTypes.arrayOf(propTypes.object).isRequired,
		swatches: propTypes.arrayOf(propTypes.object).isRequired,
		enableSwatches: propTypes.bool.isRequired,
		autoplay: propTypes.shape().isRequired,
		swatchTextColor: propTypes.string.isRequired,
		paginationThumbnails: propTypes.shape()
	}).isRequired
};

ImageCarousel.propTypes = ImageCarouselProps;
